import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X } from "lucide-react";

const NAV_LINKS = [
  { label: "Startseite", href: "#hero" },
  { label: "Leistungen", href: "#leistungen" },
  { label: "Abrechnung", href: "#abrechnung" },
  { label: "Über mich", href: "#ueber-mich" },
  { label: "Kontakt", href: "#kontakt" },
];

export default function Navbar({ onOpenCookieSettings }) {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 16);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleAnchor = (e, href) => {
    e.preventDefault();
    setOpen(false);
    if (location.pathname !== "/") {
      navigate("/" + href);
      return;
    }
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? "glass-nav py-3" : "py-5 bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 flex items-center justify-between">
        {/* Logo / Brand */}
        <Link
          to="/"
          data-testid="nav-logo"
          className="flex items-center gap-3 group"
        >
          <span className="w-9 h-9 rounded-full flex items-center justify-center" style={{ background: "#F4ECDD" }}>
            <svg width="22" height="22" viewBox="0 0 32 32" fill="none" aria-hidden="true">
              <g stroke="#6B8AA8" strokeWidth="1" strokeLinecap="round">
                <line x1="16" y1="3" x2="16" y2="13" />
                <line x1="9" y1="5" x2="13" y2="13" />
                <line x1="23" y1="5" x2="19" y2="13" />
                <line x1="5" y1="10" x2="11" y2="14" />
                <line x1="27" y1="10" x2="21" y2="14" />
                <line x1="4" y1="15" x2="11" y2="15" />
                <line x1="28" y1="15" x2="21" y2="15" />
              </g>
              <circle cx="16" cy="18" r="1.5" fill="#6B8AA8" />
            </svg>
          </span>
          <div className="flex flex-col leading-none">
            <span className="font-serif text-xl" style={{ color: "var(--ink)" }}>Praxis Seelen-Raum</span>
          </div>
        </Link>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-9" aria-label="Hauptnavigation">
          {NAV_LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              onClick={(e) => handleAnchor(e, l.href)}
              className="soft-link text-[0.92rem]"
            >
              {l.label}
            </a>
          ))}
          <a
            href="#kontakt"
            onClick={(e) => handleAnchor(e, "#kontakt")}
            data-testid="nav-cta-termin"
            className="btn-primary !py-2.5 !px-5 !text-sm"
          >
            Termin anfragen
          </a>
        </nav>

        {/* Mobile toggle */}
        <button
          data-testid="mobile-menu-toggle"
          className="lg:hidden p-2 rounded-full hover:bg-[color:var(--beige-soft)] transition-colors"
          onClick={() => setOpen((v) => !v)}
          aria-label={open ? "Menü schließen" : "Menü öffnen"}
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {/* Mobile drawer */}
      <div
        data-testid="mobile-drawer"
        className={`lg:hidden absolute left-0 right-0 top-full mobile-drawer ${open ? "open" : ""}`}
        style={{ visibility: open ? "visible" : "hidden", pointerEvents: open ? "auto" : "none" }}
      >
        <div className="mx-4 mt-3 bg-white rounded-3xl border p-6 shadow-xl" style={{ borderColor: "var(--line)" }}>
          <nav className="flex flex-col gap-1" aria-label="Mobile Navigation">
            {NAV_LINKS.map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={(e) => handleAnchor(e, l.href)}
                data-testid={`mobile-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className="px-3 py-3 rounded-xl hover:bg-[color:var(--cream-deep)] transition-colors text-[0.95rem]"
              >
                {l.label}
              </a>
            ))}
            <a
              href="#kontakt"
              onClick={(e) => handleAnchor(e, "#kontakt")}
              data-testid="mobile-cta-termin"
              className="btn-primary mt-4 w-full"
            >
              Termin anfragen
            </a>
          </nav>
        </div>
      </div>
    </header>
  );
}
